import Collapse from '@mui/material/Collapse';
import {useTheme} from '@mui/material/styles';
import {useBoolean} from 'minimal-shared/hooks';
import {mergeClasses} from 'minimal-shared/utils';
import {useTranslation} from 'react-i18next';

import {useWorkspace} from '@app/store';
import {menus} from '@workspaces/menus';

import {Nav, NavLi, NavSubheader, NavUl} from '../components';
import {navSectionClasses, navSectionCssVars} from '../styles';
import type {NavGroupProps, NavSectionProps} from '../types';
import {NavList} from './nav-list';

// ----------------------------------------------------------------------

export function NavSectionVertical({
  sx,
  render,
  className,
  slotProps,
  currentRole,
  enabledRootRedirect,
  cssVars: overridesVars,
  ...other
}: NavSectionProps) {
  const theme = useTheme();
  const workspace = useWorkspace((state) => state.workspace);

  const data = workspace ? menus[workspace.id] : [];

  const cssVars = {...navSectionCssVars.vertical(theme), ...overridesVars};

  return (
    <Nav
      className={mergeClasses([navSectionClasses.vertical, className])}
      sx={[{...cssVars}, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      <NavUl sx={{flex: '1 1 auto', gap: 'var(--nav-item-gap)'}}>
        {data.map((group) => (
          <Group
            key={group.subheader ?? group.items[0].title}
            subheader={group.subheader}
            items={group.items}
            render={render}
            slotProps={slotProps}
            currentRole={currentRole}
            enabledRootRedirect={enabledRootRedirect}
          />
        ))}
      </NavUl>
    </Nav>
  );
}

// ----------------------------------------------------------------------

function Group({items, render, subheader, slotProps, currentRole, enabledRootRedirect}: NavGroupProps) {
  const {t} = useTranslation();
  const groupOpen = useBoolean(true);

  const renderContent = () => (
    <NavUl sx={{gap: 'var(--nav-item-gap)'}}>
      {items.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={1}
          slotProps={slotProps}
          currentRole={currentRole}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );

  return (
    <NavLi>
      {subheader ? (
        <>
          <NavSubheader
            data-title={subheader}
            open={groupOpen.value}
            onClick={groupOpen.onToggle}
            sx={slotProps?.subheader}
          >
            {t(subheader)}
          </NavSubheader>

          <Collapse in={groupOpen.value}>{renderContent()}</Collapse>
        </>
      ) : (
        renderContent()
      )}
    </NavLi>
  );
}
